import { useEffect, useRef, useState } from "react";
import {
  Grid,
  GridItem,
  Box,
  Stack,
  Flex,
  SkeletonText,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { useBeforeUnload } from "../../hooks/useBeforeReload";
import AnalysisPanelContainer from "../../containers/analyse/AnalysisPanelContainer";
import { AnalysisTreeContextProvider } from "../../context/AnalysisTreeContext";
import { useAnalysisDrawerContext } from "../../context/AnalysisDrawerContext";
import { useLocation, useParams } from "react-router-dom";
import { cancelTransaction } from "../../services/AnalysisTreeServices";
import { useURLContext } from "../../context/URLContext";
import { CancelRequest, StateKey } from "build-ts/stateful_api";
import ChatBot from "../../components/chat/ChatBot";
import { getReport } from "services/ReportPageServices";
import AnalysisNavigationBar from "../../components/analyse/AnalysisNavigationBar";
import { useChat } from "context/ChatBotContext";
import { MetadataTypeEnum, metadataTypeEnumFromJSON } from "build-ts/enums";
import { getJobrun } from "services/BatchServices";
import { JobRunStep_StepType } from "build-ts/jobs_interface";
import { getScheduledJobRun } from "services/ScheduleServices";

const AnalysePage: React.FC = () => {
  useBeforeUnload();

  //treeId from url
  const { id } = useParams();
  const location = useLocation();
  const { transactionId, metadataId, metadataType, runId, treeIds } = useURLContext();

  const { 
    onOpen, 
    btnRef, 
    responseData, 
    setResponseData,
    setSelectedUuid,
    selectedUuid,
    setTreeIds,
    setTransactionId
  } = useAnalysisDrawerContext();

  const refs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [visibleUuid, setVisibleUuid] = useState<string | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const { setTrees, setMetadataId, setUserId } = useChat(); 

  const handleDeleteTree = async (uuid: string) => {
    const updatedResponse = { ...responseData };
    delete updatedResponse[uuid];
    setResponseData(updatedResponse);
    //request array of generate tree
    const transationId: StateKey[] = [{transaction_id: uuid}];

    const statefulCancelRequest: CancelRequest ={
      state_key: transationId,
    };
    await cancelTransaction(statefulCancelRequest);
  };

  // It fetches all tree IDs associated with the report and then displays them in the analysis drawer as well as in the analysis page
  useEffect(() => {
    if (metadataTypeEnumFromJSON(metadataType) === MetadataTypeEnum.REPORT_BOOK) {
      const fetchReportAndSetTreeIds = async () => {
        const report = await getReport(metadataId, metadataType);
        if(!report){
          return;
        }
        const treeIds = report.pages.flatMap(page => page.tree_headers.map(tree => tree.id)).filter((id): id is string => id !== undefined);
        setTreeIds(treeIds);
        setTrees(treeIds); // setting tree ids to send to tisa
        setMetadataId(metadataId); // setting metadata id to send to tisa
      };
      fetchReportAndSetTreeIds();
    }
    if (metadataTypeEnumFromJSON(metadataType) === MetadataTypeEnum.ANALYSIS_TREE) {
      setTreeIds([metadataId]);
      setTrees([metadataId]);
      setMetadataId(metadataId);
    }
    if (metadataTypeEnumFromJSON(metadataType) === MetadataTypeEnum.JOB_RUN) {
      const fetchJobRunAndSetTreeIds = async () => {
        const [jobId, jobRunId] = metadataId.split("|");
        const jobRun = await getJobrun(jobId, jobRunId);
        if(!jobRun){
          return;
        }
        const treeIds = jobRun?.steps.filter(step => step.type === JobRunStep_StepType.ANALYSIS_STEP).map(step => step.output).filter((id): id is string => id !== undefined);
        setTreeIds(treeIds);
        setTrees(treeIds);
        setMetadataId(jobRunId);
      };
      fetchJobRunAndSetTreeIds();
    }
    if(metadataTypeEnumFromJSON(metadataType) === MetadataTypeEnum.CUSTOM_AGENT){
      const fetchTreeIds = async () => {
        const agent = await getScheduledJobRun(runId);
        if(agent){
          const treeIds = agent?.agent_run?.tree_headers.map(tree => tree.id).filter((id): id is string => id !== undefined);
          if(treeIds){
            setTreeIds(treeIds);
            setTrees(treeIds);
          }
        }
        setUserId(runId);
        setMetadataId(metadataId);
      };
      fetchTreeIds();
    }
  }, [metadataId, metadataType]);

  useEffect(() => {
    if (id) {
      setTreeIds((prev) => [...prev, id]);
      setTrees([id]);
    }
    if(transactionId){
      setTransactionId(transactionId);
    }
    if(treeIds){
      setTreeIds((prev) => [...prev, ...treeIds]);
      setTrees(treeIds);
    }
  }, [id, onOpen, location.search]);

  useEffect(() => {
    if (selectedUuid && refs.current[selectedUuid]) {
      refs.current[selectedUuid]!.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedUuid]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleUuid(entry.target.id);
          }
        });
      },
      { threshold: 0.5 }
    );

    Object.keys(refs.current).forEach((uuid) => {
      if (refs.current[uuid]) {
        observer.observe(refs.current[uuid]!);
      }
    });

    return () => {
      Object.keys(refs.current).forEach((uuid) => {
        if (refs.current[uuid]) {
          observer.unobserve(refs.current[uuid]!);
        }
      });
    };
  }, [responseData]);

  return (
    <Grid 
      templateColumns={isChatOpen ? "70% 30%" : "100% 0%"} 
      height="100%"
      bg="gray.100" 
      p={1} 
      gap={1}
      overflowY="auto"
      overflowX="hidden"
      transition="all 0.3s ease-in-out"
    >
      {/* navigation menu  */}
      <AnalysisNavigationBar
        isNavOpen={isNavOpen}
        setIsNavOpen={setIsNavOpen}
        responseData={responseData}
        setResponseData={setResponseData}
        visibleUuid={visibleUuid}
        setSelectedUuid={setSelectedUuid}
        handleDeleteTree={handleDeleteTree}
        btnRef={btnRef}
        onOpen={onOpen}
      />

      {/* Analysis panel  */}
      <GridItem overflow="hidden">
        {Object.keys(responseData).map((uuid) => (
          <Stack key={uuid} ref={(el) => (refs.current[uuid] = el)} id={uuid} height="100%">
            {responseData[uuid]?.loading ? (
              <Box
                key={uuid}
                p="6"
                boxShadow="lg"
                bg="bg.surface"
                borderRadius="lg"
                height="100%"
              >
                <Flex
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                >
                  <Spinner size="lg" color="#4e1e6c" thickness="3px" />
                  <Text mt="2" fontSize="lg" fontWeight="semibold">
                    Loading Data...
                  </Text>
                </Flex>
                <SkeletonText mt="4" noOfLines={6} spacing="4" />
              </Box>
            ) : responseData[uuid]?.tree?.header ? (
              <AnalysisTreeContextProvider 
                transactionId={uuid} 
                tree={responseData[uuid]?.tree!}
                nodeMetricArray={responseData[uuid]?.node_metric_array!}
              >
                <AnalysisPanelContainer
                  key={uuid}
                  onToggleChat={() => setIsChatOpen(!isChatOpen)}
                  isChatOpen={isChatOpen}
                />
              </AnalysisTreeContextProvider>
            ) : null}
          </Stack>
        ))}
      </GridItem>

      {/* Chat panel */}
      <GridItem 
        overflow="hidden"
        transition="all 0.3s ease-in-out"
        transform={isChatOpen ? "translateX(0)" : "translateX(100%)"}
        opacity={isChatOpen ? 1 : 0}
        bg="white"
        boxShadow="md"
        borderRadius="lg"
        m={1}
        ml={0}
      >
      <ChatBot onToggleChat={() => setIsChatOpen(!isChatOpen)}/>
      </GridItem>
    </Grid>
  );
};

export default AnalysePage;